import { apiClient } from '@/shared/api/api.client'

// ─── Type definitions ───────────────────────────────────────────────────────

export interface FileRecord {
	fileId: string
	fileName: string
	fileSize: number | null
	contentType: string
	fileCategory: string | null
	checksum: string | null
	s3Key: string
	s3Bucket: string
	status: string
	idempotencyKey: string | null
	entityType: string | null
	entityId: string | null
	uploadedBy: string
	description: string | null
	isPublic: boolean
	isActive: boolean
	processingStatus: string | null
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

export interface FileShareRecord {
	shareId: string
	fileId: string
	grantedTo: string
	permission: string
	grantedBy: string
	expiresAt: string | null
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

export interface UploadUrlResponse {
	file: FileRecord
	uploadUrl: string
	expiresIn: number
}

export interface DownloadUrlResponse {
	downloadUrl: string
	fileName: string
}

export interface FileFilters {
	entityType?: string
	entityId?: string
	isPublic?: string
	fileCategory?: string
	sharedWithMe?: string
	page?: number
	limit?: number
}

export interface UploadUrlInput {
	fileName: string
	contentType: string
	checksum?: string
	entityType?: string
	entityId?: string
	isPublic?: boolean
	description?: string
	idempotencyKey?: string
}

export interface FileUpdateInput {
	description?: string
	entityType?: string
	entityId?: string
}

export interface FileShareInput {
	userId: string
	permission: string
	expiresAt?: string
}

// ─── API surface ────────────────────────────────────────────────────────────

function buildFilesQuery(filters: FileFilters): string {
	const params = new URLSearchParams()
	if (filters.entityType) params.set('filters[entityType]', filters.entityType)
	if (filters.entityId) params.set('filters[entityId]', filters.entityId)
	if (filters.isPublic) params.set('filters[isPublic]', filters.isPublic)
	if (filters.fileCategory) params.set('filters[fileCategory]', filters.fileCategory)
	if (filters.sharedWithMe) params.set('filters[sharedWithMe]', filters.sharedWithMe)
	if (filters.page) params.set('page', String(filters.page))
	if (filters.limit) params.set('limit', String(filters.limit))
	return params.toString()
}

export const filesApi = {
	list: async (filters: FileFilters = {}): Promise<FileRecord[]> => {
		const response = await apiClient.get<FileRecord[]>(`/files?${buildFilesQuery(filters)}`)
		return response.data || []
	},
	get: async (fileId: string): Promise<FileRecord> => {
		const response = await apiClient.get<FileRecord>(`/files/${fileId}`)
		return response.data
	},
	requestUploadUrl: async (data: UploadUrlInput): Promise<UploadUrlResponse> => {
		const response = await apiClient.post<UploadUrlResponse>('/files/upload-url', data)
		return response.data
	},
	confirmUpload: async (fileId: string): Promise<FileRecord> => {
		const response = await apiClient.post<FileRecord>(`/files/${fileId}/confirm-upload`)
		return response.data
	},
	getDownloadUrl: async (fileId: string): Promise<DownloadUrlResponse> => {
		const response = await apiClient.get<DownloadUrlResponse>(`/files/${fileId}/download-url`)
		return response.data
	},
	update: async (fileId: string, data: FileUpdateInput): Promise<FileRecord> => {
		const response = await apiClient.patch<FileRecord>(`/files/${fileId}`, data)
		return response.data
	},
	delete: async (fileId: string): Promise<void> => {
		await apiClient.delete(`/files/${fileId}`)
	},
	listShares: async (fileId: string): Promise<FileShareRecord[]> => {
		const response = await apiClient.get<FileShareRecord[]>(`/files/${fileId}/shares`)
		return response.data || []
	},
	createShare: async (fileId: string, data: FileShareInput): Promise<FileShareRecord> => {
		const response = await apiClient.post<FileShareRecord>(`/files/${fileId}/shares`, data)
		return response.data
	},
	revokeShare: async (fileId: string, shareId: string): Promise<void> => {
		await apiClient.delete(`/files/${fileId}/shares/${shareId}`)
	},
}
